/**
 * Finance View Render Functions
 * Contains all top-level finance view rendering functions
 */
import React from 'react';
import { Divider } from 'primereact/divider';
import { InstallmentWiseTable } from './InstallmentWiseTable';
import { FeetypeWiseTable } from './FeetypeWiseTable';
import { ConcessionsTable } from './ConcessionsTable';
import { UnassignFeesTable } from './UnassignFeesTable';
import { MonthWiseTable } from './MonthWiseTable';
import { FeesWithRefundTable } from './FeesWithRefundTable';
import { TransactionsTable } from './TransactionsTable';
import { FeeAccountTable } from './FeeAccountTable';
import { PaymentModeTable } from './PaymentModeTable';
import { AgingTable } from './AgingTable';
import { OnlinePaymentsTable } from './OnlinePaymentsTable';
import { OtherFeesTable } from './OtherFeesTable';
import { ChequePaymentsTable } from './ChequePaymentsTable';
import { AdvancesTable } from './AdvancesTable';
import { AdvanceReceiptsTable } from './AdvanceReceiptsTable';
import { AdvanceTransactionsTable } from './AdvanceTransactionsTable';
import { ChequeBouncePaymentsTable } from './ChequeBouncePaymentsTable';
import { ChequeBounceTransactionsTable } from './ChequeBounceTransactionsTable';
import { PDCInstallmentWiseTable } from './PDCInstallmentWiseTable';
import { PDCMonthWiseTable } from './PDCMonthWiseTable';
import { PDCInstallmentFeetypeTable } from './PDCInstallmentFeetypeTable';
import { PDCDetailsPopup } from './PDCDetailsPopup';
import { TransportFeeManagement } from './TransportFeeManagement';
import { CancelledTransactionsTable } from './CancelledTransactionsTable';
import ClassetDropdown from '../../../classetComponents/classetDropDown';
import { Button } from 'primereact/button';

/**
 * Render Receipt Entry view
 */
export const renderReceiptEntryView = (studentData) => {
    return (
        <div className="finance-view-container">
            <h2>Receipt Entry</h2>
            <Divider />
            <div className="finance-content">
                <p>Receipt Entry content for {studentData?.firstName} {studentData?.lastName}</p>
            </div>
        </div>
    );
};

/**
 * Render Fee Details view
 */
export const renderFeeDetailsView = (studentData, state, setState, navigate = null) => {
    const activeFeeDetailsTab = state?.activeFeeDetailsTab || 'feetypeWise';
    const feeData = state?.feetypeWiseFeeData || null;
    const rawData = state?.feeTermsRawData || null;
    const isLoading = state?.feetypeWiseFeeLoading || false;
    const feeHistoryData = state?.feeHistoryData || null;
    const feeHistoryLoading = state?.feeHistoryLoading || false;

    const renderTabContent = () => {
        switch (activeFeeDetailsTab) {
            case 'feetypeWise':
                return <FeetypeWiseTable feeData={feeData} isLoading={isLoading} studentData={studentData} navigate={navigate} />;
            case 'installmentWise':
                return <InstallmentWiseTable rawApiData={rawData} isLoading={isLoading} />;
            case 'monthWise':
                return <MonthWiseTable studentData={studentData} feeHistoryData={feeHistoryData} isLoading={feeHistoryLoading} />;
            case 'feesWithRefund':
                return <FeesWithRefundTable studentData={studentData} feeHistoryData={feeHistoryData} isLoading={feeHistoryLoading} />;
            case 'concessions':
                return <ConcessionsTable feeHistoryData={feeHistoryData} isLoading={feeHistoryLoading} studentData={studentData} />;
            case 'unassignFees':
                return <UnassignFeesTable studentData={studentData} feeHistoryData={feeHistoryData} isLoading={feeHistoryLoading} />;
            default:
                return null;
        }
    };

    return (
        <div className="finance-view-container">
            <h2>Fee Details</h2>
            <Divider />

            <div className="fee-details-tabs">
                <div className="fee-tabs-menu">
                    <div
                        className={`fee-tab-item ${activeFeeDetailsTab === 'feetypeWise' ? 'active' : ''}`}
                        onClick={() => setState({ activeFeeDetailsTab: 'feetypeWise' })}
                    >
                        Feetype Wise
                    </div>
                    <div
                        className={`fee-tab-item ${activeFeeDetailsTab === 'installmentWise' ? 'active' : ''}`}
                        onClick={() => setState({ activeFeeDetailsTab: 'installmentWise' })}
                    >
                        Installment Wise
                    </div>
                    <div
                        className={`fee-tab-item ${activeFeeDetailsTab === 'monthWise' ? 'active' : ''}`}
                        onClick={() => setState({ activeFeeDetailsTab: 'monthWise' })}
                    >
                        Month Wise
                    </div>
                    <div
                        className={`fee-tab-item ${activeFeeDetailsTab === 'feesWithRefund' ? 'active' : ''}`}
                        onClick={() => setState({ activeFeeDetailsTab: 'feesWithRefund' })}
                    >
                        Fees with Refund Details
                    </div>
                    <div
                        className={`fee-tab-item ${activeFeeDetailsTab === 'concessions' ? 'active' : ''}`}
                        onClick={() => setState({ activeFeeDetailsTab: 'concessions' })}
                    >
                        Concessions
                    </div>
                    <div
                        className={`fee-tab-item ${activeFeeDetailsTab === 'unassignFees' ? 'active' : ''}`}
                        onClick={() => setState({ activeFeeDetailsTab: 'unassignFees' })}
                    >
                        Unassign Fees
                    </div>
                </div>

                <div className="fee-tabs-content">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

/**
 * Render View Transactions view
 */
export const renderViewTransactionsView = (studentData, state, setState, toast, academicYear, transactionsData, transactionsLoading, cancelledTransactionsData, cancelledTransactionsLoading) => {
    const activeTransactionsTab = state?.activeTransactionsTab || 'transactions';

    const renderTabContent = () => {
        switch (activeTransactionsTab) {
            case 'transactions':
                return <TransactionsTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'feeAccount':
                return <FeeAccountTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'paymentMode':
                return <PaymentModeTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'aging':
                return <AgingTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'onlinePayments':
                return <OnlinePaymentsTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'otherFees':
                return <OtherFeesTable studentData={studentData} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'chequePayments':
                return <ChequePaymentsTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'cancelled':
                return <CancelledTransactionsTable studentData={studentData} academicYear={academicYear} toast={toast} cancelledTransactionsData={cancelledTransactionsData} cancelledTransactionsLoading={cancelledTransactionsLoading} />;
            case 'advances':
                return (
                    <div className="advances-content">
                        <AdvancesTable
                            studentData={studentData}
                            academicYear={academicYear}
                            toast={toast}
                            transactionsData={transactionsData}
                            transactionsLoading={transactionsLoading}
                        />
                    </div>
                );
            case 'schoolStore':
                return (
                    <div className="school-store-content">
                        <p>School Store tab content</p>
                        {/* School Store content will go here */}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="finance-view-container">
            <h2>View Transactions</h2>
            <Divider />

            <div className="view-transactions-tabs">
                <div className="transactions-tabs-menu">
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'transactions' })}
                    >
                        Transactions
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'feeAccount' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'feeAccount' })}
                    >
                        Fee Account
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'paymentMode' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'paymentMode' })}
                    >
                        Payment Mode
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'aging' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'aging' })}
                    >
                        Aging
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'onlinePayments' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'onlinePayments' })}
                    >
                        Online Payments
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'otherFees' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'otherFees' })}
                    >
                        Other Fees
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'chequePayments' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'chequePayments' })}
                    >
                        Cheque Payments
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'cancelled' ? 'active' : ''}`}
                        onClick={() => {
                            console.log('Cancelled tab clicked - setting state');
                            setState({ activeTransactionsTab: 'cancelled' });
                        }}
                    >
                        Cancelled
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'advances' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'advances' })}
                    >
                        Advances
                    </div>
                    <div
                        className={`transaction-tab-item ${activeTransactionsTab === 'schoolStore' ? 'active' : ''}`}
                        onClick={() => setState({ activeTransactionsTab: 'schoolStore' })}
                    >
                        School Store
                    </div>
                </div>

                <div className="transactions-tabs-content">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};


/**
 * Render Fee Management view
 */
export const renderFeeManagementView = (studentData) => {
    return (
        <div className="finance-view-container">
            <h2>Fee Management</h2>
            <Divider />
            <div className="finance-content">
                <p>Fee Management content for {studentData?.firstName} {studentData?.lastName}</p>
            </div>
        </div>
    );
};

/**
 * Render View Cheque Payments view
 */
export const renderViewChequePaymentsView = (studentData, state, setState, toast, academicYear, transactionsData, transactionsLoading) => {
    return (
        <div className="finance-view-container">
            <h2>View Cheque Payments</h2>
            <Divider />
            <div className="view-transactions-tabs">
                <div className="transactions-tabs-content">
                    <ChequePaymentsTable
                        studentData={studentData}
                        academicYear={academicYear}
                        toast={toast}
                        transactionsData={transactionsData}
                        transactionsLoading={transactionsLoading}
                        showTabWrapper={false}
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * Render Transport Fee Management view
 */
export const renderTransportFeeManagementView = (studentData, isLoading = false, toast = null, navigate = null, feeTermsRawData = null, feetypeWiseFeeLoading = false) => {
    return (
        <div className="finance-view-container">
            <h2>Transport Fee Management</h2>
            <Divider />
            <TransportFeeManagement 
                studentData={studentData} 
                isLoading={isLoading} 
                toast={toast} 
                navigate={navigate}
                feeTermsRawData={feeTermsRawData}
                feetypeWiseFeeLoading={feetypeWiseFeeLoading}
            />
        </div>
    );
};

/**
 * Render Cheque Bounce Charges view
 */
export const renderChequeBounceChargesView = (studentData, state, setState, toast, academicYear, transactionsData, transactionsLoading) => {
    const activeChequeBounceTab = state?.activeChequeBounceTab || 'chequeBouncePayments';

    const renderTabContent = () => {
        switch (activeChequeBounceTab) {
            case 'chequeBouncePayments':
                return <ChequeBouncePaymentsTable 
                    studentData={studentData} 
                    academicYear={academicYear} 
                    toast={toast} 
                    transactionsData={transactionsData}
                    transactionsLoading={transactionsLoading}
                />;
            case 'chequeBounceTransactions':
                return <ChequeBounceTransactionsTable 
                    studentData={studentData} 
                    academicYear={academicYear} 
                    toast={toast}
                    transactionsData={transactionsData}
                    transactionsLoading={transactionsLoading}
                />;
            default:
                return null;
        }
    };

    return (
        <div className="finance-view-container">
            <h2>Cheque Bounce Charges</h2>
            <Divider />

            <div className="view-transactions-tabs">
                <div className="transactions-tabs-menu">
                    <div
                        className={`transaction-tab-item ${activeChequeBounceTab === 'chequeBouncePayments' ? 'active' : ''}`}
                        onClick={() => setState({ activeChequeBounceTab: 'chequeBouncePayments' })}
                    >
                        Cheque Bounce Payments
                    </div>
                    <div
                        className={`transaction-tab-item ${activeChequeBounceTab === 'chequeBounceTransactions' ? 'active' : ''}`}
                        onClick={() => setState({ activeChequeBounceTab: 'chequeBounceTransactions' })}
                    >
                        Cheque Bounce Transactions
                    </div>
                </div>

                <div className="transactions-tabs-content">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

/**
 * Render Post Dated Cheque Payments view
 */
export const renderPostDatedChequePaymentsView = (studentData, state, setState, transactionsData, transactionsLoading) => {
    const selectedPDCType = state?.selectedPDCType || 'Installment Wise';
    const showPDCDetailsPopup = state?.showPDCDetailsPopup || false;
    const academicYear = studentData?.academicYear || localStorage.getItem('userAcademicYear') || '2025-2026 (Class XII - XII-A)';

    const pdcTypeOptions = [
        { label: 'Installment Wise', value: 'Installment Wise' },
        { label: 'Month Wise', value: 'Month Wise' },
        { label: 'Installment with Feetype Wise', value: 'Installment with Feetype Wise' }
    ];

    const renderTableContent = () => {
        switch (selectedPDCType) {
            case 'Installment Wise':
                return <PDCInstallmentWiseTable pdcData={[]} />;
            case 'Month Wise':
                return <PDCMonthWiseTable 
                    pdcData={[]} 
                    onViewDetails={(rowData) => {
                        console.log('View PDC Details:', rowData);
                        setState({ showPDCDetailsPopup: true });
                    }}
                />;
            case 'Installment with Feetype Wise':
                return <PDCInstallmentFeetypeTable pdcData={[]} />;
            default:
                return null;
        }
    };

    return (
        <div className="finance-view-container">
            <h2>Post Dated Cheque Payments</h2>
            <Divider />

            <div className="pdc-controls-section">
                <div className="pdc-filters">
                    
                    <div className="filter-group">
                        <ClassetDropdown
                            label="Type :"
                            value={selectedPDCType}
                            options={pdcTypeOptions}
                            onChange={(e) => setState({ selectedPDCType: e.value })}
                            placeholder="Select Type"
                            className="pdc-type-dropdown"
                            width="300px"
                            height="40px"
                            showClear={false}
                        />
                    </div>

            {/*         <Button
                        label="Get"
                        className="p-button"
                        style={{ 
                            backgroundColor: '#076EFF', 
                            border: 'none',
                            height: '30px',
                            marginTop: '24px'
                        }}
                        onClick={() => {
                            console.log('Fetching PDC data for:', selectedPDCType);
                            // API call will go here
                        }}
                    /> */}
                </div>

                <div className="pdc-actions">
                    <Button
                        label="View PDC Details"
                        className="p-button-outlined"
                        style={{ 
                            color: '#076EFF', 
                            borderColor: '#076EFF',
                            height: '40px'
                        }}
                        onClick={() => setState({ showPDCDetailsPopup: true })}
                    />
                </div>
            </div>

            <div className="pdc-table-content">
                {renderTableContent()}
            </div>

            {showPDCDetailsPopup && (
                <PDCDetailsPopup
                    visible={showPDCDetailsPopup}
                    onHide={() => setState({ showPDCDetailsPopup: false })}
                    transactionsData={transactionsData}
                />
            )}
        </div>
    );
};

/**
 * Render Advance Payments view
 */
export const renderAdvancePaymentsView = (studentData, state, setState, toast, academicYear, transactionsData, transactionsLoading) => {
    const activeAdvancePaymentsTab = state?.activeAdvancePaymentsTab || 'advanceReceipts';

    const renderTabContent = () => {
        switch (activeAdvancePaymentsTab) {
            case 'advanceReceipts':
                return <AdvanceReceiptsTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            case 'advanceTransactions':
                return <AdvanceTransactionsTable studentData={studentData} academicYear={academicYear} toast={toast} transactionsData={transactionsData} transactionsLoading={transactionsLoading} />;
            default:
                return null;
        }
    };

    return (
        <div className="finance-view-container">
            <h2>Advance Payments</h2>
            <Divider />

            <div className="view-transactions-tabs">
                <div className="transactions-tabs-menu">
                    <div
                        className={`transaction-tab-item ${activeAdvancePaymentsTab === 'advanceReceipts' ? 'active' : ''}`}
                        onClick={() => setState({ activeAdvancePaymentsTab: 'advanceReceipts' })}
                    >
                        Advance receipts and Adjustments
                    </div>
                    <div
                        className={`transaction-tab-item ${activeAdvancePaymentsTab === 'advanceTransactions' ? 'active' : ''}`}
                        onClick={() => setState({ activeAdvancePaymentsTab: 'advanceTransactions' })}
                    >
                        Advance Transactions
                    </div>
                </div>

                <div className="transactions-tabs-content">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

/**
 * Render Edit Fee Amount view
 */
export const renderEditFeeAmountView = (studentData) => {
    return (
        <div className="finance-view-container">
            <h2>Edit Fee Amount</h2>
            <Divider />
            <div className="finance-content">
                <p>Edit Fee Amount content for {studentData?.firstName} {studentData?.lastName}</p>
            </div>
        </div>
    );
};

/**
 * Render Student Deposits / Transactions view
 */
export const renderStudentDepositsTransactionsView = (studentData) => {
    return (
        <div className="finance-view-container">
            <h2>Student Deposits / Transactions</h2>
            <Divider />
            <div className="finance-content">
                <p>Student Deposits / Transactions content for {studentData?.firstName} {studentData?.lastName}</p>
            </div>
        </div>
    );
};

/**
 * Render Loan Payments view
 */
export const renderLoanPaymentsView = (studentData) => {
    return (
        <div className="finance-view-container">
            <h2>Loan Payments</h2>
            <Divider />
            <div className="finance-content">
                <p>Loan Payments content for {studentData?.firstName} {studentData?.lastName}</p>
            </div>
        </div>
    );
};
